// Constants
import {
  CALCULATION_VEHICLE_GROUP_CREATE_PAGE_TITLE,
  CALCULATION_VEHICLE_GROUP_DETAIL_PAGE_TITLE,
  CALCULATION_VEHICLE_GROUP_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Types
import { VehicleGroupInfoCardProps } from "./types";
import { VehicleGroupDetailFormValues } from "services/api/calculations/vehiclegroup/types";

// Services & API Calls
import { getVehicleGroupAPI } from "services/api/calculations/vehiclegroup/api";

// React and State Management
import { memo, useCallback, useEffect, useState } from "react";

// UI Components
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Loader } from "ui/components/Loader";

// Helpers
import { useTranslate } from "helpers";

// Constants/Initial Values
import { initialVehicleGroupDetailForm } from "services/api/calculations/vehiclegroup/consts";

// Validation
import { Errors } from "services/forms/validation/types";

// Custom Components
import VehicleGroupEditCard from "../../../VehicleGroupEditCard/VehicleGroupEditCard";

const VehicleGroupInfoCard: React.FC<VehicleGroupInfoCardProps> = (props) => {
  const { vehicleGroupId } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<VehicleGroupDetailFormValues>(
    initialVehicleGroupDetailForm
  );

  useEffect(() => {
    if (vehicleGroupId) {
      fetchVehicleGroup(vehicleGroupId);
    }
  }, [vehicleGroupId]);

  const fetchVehicleGroup = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialVehicleGroupDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getVehicleGroupAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleEditSuccess = useCallback(() => {
    if (vehicleGroupId) fetchVehicleGroup(vehicleGroupId);
    setShowEditModal(false);
  }, []);

  if (!vehicleGroupId) return <></>;

  return (
    <>
      <div className="rounded-sm bg-white border max-w-lg w-full">
        <div className="divide-y divide-slate-200">
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase">
              {t(CALCULATION_VEHICLE_GROUP_DETAIL_PAGE_TITLE.text)}
            </p>
            <div className="flex items-center gap-2 px-3 my-3 ">
              <Button
                size="small"
                variant="primary"
                children={t("admin.change")}
                onClick={() => setShowEditModal(true)}
                startIcon="/media/icons/plus.svg"
              />
            </div>
          </div>
          <div className="px-4 py-2 min-h-[56px]">
            <div className="text-xs font-medium uppercase text-slate-600">
              {t("admin.calculation.vehiclegroup.detail.name")}
            </div>
            <div className="text-sm text-slate-500 truncate">
              {formValues.name}
            </div>
          </div>
          <div className="px-4 py-2 min-h-[56px]">
            <div className="text-xs font-medium uppercase text-slate-600">
              {t("admin.calculation.vehiclegroup.detail.parent")}
            </div>
            <div className="text-sm text-slate-500 truncate">
              {formValues.parentVehicleGroupName}
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <ModalHeader
            title={
              vehicleGroupId
                ? CALCULATION_VEHICLE_GROUP_EDIT_PAGE_TITLE.text
                : CALCULATION_VEHICLE_GROUP_CREATE_PAGE_TITLE.text
            }
            onHide={() => setShowEditModal(false)}
          />
          <ModalBody>
            <VehicleGroupEditCard
              vehicleGroupId={vehicleGroupId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};

export default memo(VehicleGroupInfoCard);
